import React from "react";
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { StyleEnums } from "models/enums";
import { EntityIdParams } from "models/propTypes/urlParamsTypes";
import { ImageMeta } from "models/propTypes/fileViewerPropTypes";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import {
  FileViewerContainer,
  PageLayouts,
  EntityTitle,
  useMountedState,
  useNavigationHistory,
  Skeletons,
  LoadingWithLogo,
  RouteBreadCrumb,
  GenericCommentsView,
} from "components/common";
import GenericDocumentVouchers from "./genericDocumentVouchers";
import GenericDocumentStateFlow from "./genericDocumentStateFlow";
import GenericDocumentFooter from "./genericDocumentFooter";
import { ErrorView } from "components/error";
import { useParams } from "react-router-dom";
import { useImageService, useGenericDocumentService } from "serviceHooks";
import useGenericDocumentPermissions from "./hooks/useGenericDocumentPermissions";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const GenericDocumentView: React.FC = () => {
  const imageService = useImageService();
  const genericDocumentService = useGenericDocumentService();
  const { canViewGenericDocuments } = useGenericDocumentPermissions();

  const { getMounted } = useMountedState();
  const urlParams = useParams<EntityIdParams>();
  const { goBack } = useNavigationHistory();
  const [loading, setLoading] = React.useState(true);
  const [document, setDocument] = React.useState<WintSuperkollAPIModel.GenericDocument>();
  const [key, setKey] = React.useState(0);
  const [attachments, setAttachments] = React.useState<ImageMeta[]>([]);
  const displayType = StyleEnums.DisplayType.Info;

  const loadDocument = (documentId: number) => {
    genericDocumentService
      .get(documentId)
      .then((result) => {
        if (getMounted()) {
          setDocument(result.data);
          setKey(key + 1);
        }
      })
      .catch(() => {
        if (document)
          setTimeout(() => {
            goBack();
          }, 1000);
      })
      .finally(() => setLoading(false));
  };

  React.useEffect(() => {
    const documentId = urlParams.id;
    if (documentId) {
      if (document?.id !== Number.parseInt(documentId)) {
        setLoading(true);
        loadDocument(Number.parseInt(documentId));
      }
    } else {
      setDocument(undefined);
      setLoading(false);
    }
  }, [urlParams]);

  React.useEffect(() => {
    document &&
      imageService.getImagesMeta(document.id, WintSuperkollAPIModel.ImageType.GenericDocument).then((data) => {
        if (getMounted() && data.numberOfAttachments > 0)
          setAttachments(
            data.attachmentsMeta
              ? data.attachmentsMeta.map((meta) => ({
                  id: Number(meta.attachmentId),
                  contentType: meta.contentType,
                  numberOfPages: meta.numberOfPages,
                }))
              : []
          );
      });
  }, [document?.id, getMounted]);

  const reloadDocument = () => {
    if (document) {
      loadDocument(document.id);
    }
  };

  const markAsDeleteDocument = () => {
    goBack();
  };

  const upperLeftComponent = React.useMemo(
    () =>
      document ? (
        <EntityTitle
          subTitle={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Document.GenericDocuments)}
          title={document?.description ?? ""}
        />
      ) : (
        <></>
      ),
    [document]
  );

  const upperRightComponent = React.useMemo(
    () =>
      document ? (
        <GenericDocumentStateFlow key={`stateFlow-${document.state}`} id={document.id} />
      ) : (
        <Skeletons.StateFlowSkeleton key="steps-skeleton" stepCount={3} />
      ),
    [document]
  );

  const lowerLeftComponent = React.useMemo(
    () => (
      <FileViewerContainer
        key={key}
        meta={attachments}
        parentId={document?.id ?? 0}
        type={WintSuperkollAPIModel.ImageType.GenericDocument}
      />
    ),
    [document?.id, attachments]
  );

  return (
    <>
      <PageLayouts.EntityFullScreen
        entityKey={`generic-document`}
        id={document?.id ?? 0}
        navigation={<RouteBreadCrumb current={loading ? "" : document?.description ?? ""} showHome />}
        hasPermissions={canViewGenericDocuments}
      >
        {loading ? (
          <LoadingWithLogo />
        ) : document ? (
          <PageLayouts.EntityViewLayout
            displayType={displayType}
            upperLeftComponent={upperLeftComponent}
            upperRightComponent={upperRightComponent}
            lowerLeftComponent={lowerLeftComponent}
            footer={
              <GenericDocumentFooter
                document={document}
                reloadDocument={reloadDocument}
                markAsDeleteDocument={markAsDeleteDocument}
              />
            }
          >
            <>
              <GenericCommentsView
                key={`comments-${document.state}`}
                parentId={document.id}
                type={WintSuperkollAPIModel.GenericCommentType.GenericVoucher}
              />
              {<GenericDocumentVouchers key={`vouchers-state`} id={document.id} />}
            </>
          </PageLayouts.EntityViewLayout>
        ) : (
          <ErrorView />
        )}
      </PageLayouts.EntityFullScreen>
    </>
  );
};

export default GenericDocumentView;
